import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Mail, Loader2, UserPlus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface PilotUser {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
}

export function PilotUserList() {
  const { role, mode, dealershipId, isSolo } = useAuth();
  const [users, setUsers] = useState<PilotUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState<Record<string, boolean>>({});
  const [cooldown, setCooldown] = useState<Record<string, number>>({});
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<'rep' | 'manager'>('rep');
  const [inviteSending, setInviteSending] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const isAdmin = role === 'manager' || role === 'admin';
  const isReal = mode === 'real';

  useEffect(() => {
    if (!isAdmin || !isReal || !dealershipId) {
      setLoading(false);
      return;
    }
    let active = true;
    (async () => {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, email, full_name, dealership_id')
        .eq('dealership_id', dealershipId);
      const ids = (profiles ?? []).map((p) => p.id);
      const { data: roles } = await supabase
        .from('user_roles')
        .select('user_id, role')
        .in('user_id', ids.length ? ids : ['00000000-0000-0000-0000-000000000000']);
      const roleMap = new Map((roles ?? []).map((r) => [r.user_id, r.role as string]));
      const merged: PilotUser[] = (profiles ?? []).map((p) => ({
        id: p.id,
        email: p.email,
        full_name: p.full_name,
        role: roleMap.get(p.id) ?? 'rep',
      }));
      if (active) {
        setUsers(merged);
        setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [isAdmin, isReal, dealershipId, refreshKey]);

  // Cooldown ticker
  useEffect(() => {
    if (Object.keys(cooldown).length === 0) return;
    const t = setInterval(() => {
      setCooldown((prev) => {
        const next: Record<string, number> = {};
        const now = Date.now();
        for (const [k, v] of Object.entries(prev)) {
          if (v > now) next[k] = v;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  if (!isAdmin || !isReal || isSolo) return null;

  const handleResend = async (email: string) => {
    setSending((s) => ({ ...s, [email]: true }));
    try {
      const { data, error } = await supabase.functions.invoke('resend-invite', {
        body: { email },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success(`Invite sent to ${email}`);
      setCooldown((c) => ({ ...c, [email]: Date.now() + 30_000 }));
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to send invite');
    } finally {
      setSending((s) => ({ ...s, [email]: false }));
    }
  };

  const handleInvite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email');
      return;
    }
    setInviteSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('invite-rep', {
        body: {
          email,
          full_name: inviteName.trim(),
          role: inviteRole,
        },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success(`Invite sent to ${email}`);
      setInviteOpen(false);
      setInviteEmail('');
      setInviteName('');
      setInviteRole('rep');
      setRefreshKey((k) => k + 1);
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to send invite');
    } finally {
      setInviteSending(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-base">Team Members</CardTitle>
          <CardDescription>Invite reps and managers to your dealership</CardDescription>
        </div>
        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <UserPlus className="h-4 w-4" /> Invite
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite team member</DialogTitle>
              <DialogDescription>
                They'll receive an email to set their password and join your dealership.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="invite-name">Full name</Label>
                <Input
                  id="invite-name"
                  placeholder="Jane Doe"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="invite-email">Email</Label>
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="jane@dealership.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="invite-role">Role</Label>
                <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as 'rep' | 'manager')}>
                  <SelectTrigger id="invite-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rep">Sales Rep</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setInviteOpen(false)} disabled={inviteSending}>
                Cancel
              </Button>
              <Button onClick={handleInvite} disabled={inviteSending}>
                {inviteSending ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Sending</>
                ) : (
                  <><Mail className="h-4 w-4" /> Send invite</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : users.length === 0 ? (
          <div className="text-sm text-muted-foreground">No team members yet.</div>
        ) : (
          <div className="space-y-2">
            {users.map((u) => {
              const cdLeft = Math.max(0, Math.ceil(((cooldown[u.email] ?? 0) - Date.now()) / 1000));
              const isSending = !!sending[u.email];
              return (
                <div key={u.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{u.full_name || u.email}</p>
                    <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="secondary" className="capitalize">
                      {u.role === 'rep' ? 'Sales Rep' : u.role}
                    </Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleResend(u.email)}
                      disabled={isSending || cdLeft > 0}
                    >
                      {isSending ? (
                        <><Loader2 className="h-4 w-4 animate-spin" /> Sending</>
                      ) : cdLeft > 0 ? (
                        `Wait ${cdLeft}s`
                      ) : (
                        <><Mail className="h-4 w-4" /> Resend invite</>
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}